/*
 * Le Parfait jar opening: a three-second canvas animation built only from product photographs.
 * The lever swings out, the clasp releases, the lid swings open on its hinge, then the camera moves
 * into the open jar. One continuous timing curve drives every stage.
 *
 * Ported from 04-deliverables/jar-photo-animation-2026-09-16/source/main.js. The drawing code below
 * (camera, clasp-column warp, lid hinge projection, timing curve, additive seam compositing, warm-up
 * render) is unchanged from that file. The standalone page chrome is replaced by click-to-play card
 * behavior: see the "animation" block in sections/lp-shop-by-use.liquid.
 */
(function () {
  if (window.__lpJarOpening) return;
  window.__lpJarOpening = true;

  // Layer offsets and hinge/clasp geometry, from source/layers/geometry.json.
  const G = {"canvas":[1350,2150],"jar":[631.0,1203.0],"lidR":385.0,"paper":[222.0,221.0,218.0],"closed":{"fit":[647.666191830982,837.5154008422102,484.2711640712275],"scale":0.7950091365410589},"lever":{"fit":[663.6840782243725,663.5622403527493,429.47726641880456],"scale":0.8964386013032117},"released":{"fit":[655.1220490401286,722.9266849468864,446.4102702341371],"scale":0.8624353552575569},"openLid":{"cx":637.2649644166185,"cy":385.88856141317484,"rx":331.98453467974105,"ry":277.3273697215464},"swing":{"H":2600.0,"pivot":732.5,"thetaEnd":209.0,"optic":[675.0,900.0],"openLidScaleX":0.862297492674652,"openLidCx":637.2649644166185},"clasp":{"x0":450,"x1":812,"feather":32,"closed":[1450,1625,1700,2080],"lever":[1450,1625,1995,2080],"released":[1450,1625,1700,2080]},"layers":{"closed":{"x":120,"y":600,"w":1020,"h":1490},"lever":{"x":120,"y":600,"w":1020,"h":1490},"released":{"x":120,"y":600,"w":1020,"h":1490},"surround":{"x":120,"y":600,"w":1020,"h":1490},"lidTop":{"x":187,"y":720,"w":842,"h":881},"lidUnder":{"x":295,"y":99,"w":686,"h":646},"base":{"x":80,"y":40,"w":1190,"h":1760}}};

  const duration=3;
  const LEVER_EXTRA=1; // extra seconds given to the lever and clasp at the start
  // Timeline (seconds): lever swings out, clasp lets go, lid swings on its hinge, camera moves in.
  // One continuous progress curve drives everything; stages hand off without holds.
  // Values are fractions of that eased progress.
  const P={lever:.14,clasp:.24,swing:.6,panA:.12,panB:.64,zoomA:.42,zoomB:1};
  const PAPER=`rgb(${G.paper.join(',')})`;
  const clamp=v=>Math.max(0,Math.min(1,v));
  const ease=v=>{const t=clamp(v);return t*t*(3-2*t);};
  const between=(t,a,b)=>ease((t-a)/(b-a));
  const span=(t,a,b)=>clamp((t-a)/(b-a));
  const sine=v=>.5-.5*Math.cos(Math.PI*clamp(v));
  // cubic with chosen start/end speeds (1 = linear speed)
  const hermite=(v,v0,v1)=>{const t=clamp(v);return (v0+v1-2)*t*t*t+(3-2*v0-v1)*t*t+v0*t;};
  // The lid-and-zoom curve runs over the last (duration-LEVER_EXTRA) seconds; the lever and clasp
  // get everything before it. leverEnd is where that curve reaches P.clasp.
  const MAIN=duration-LEVER_EXTRA,mainCurve=x=>hermite(x,1.25,.35);
  const leverEnd=(()=>{let lo=0,hi=1;for(let i=0;i<40;i++){const mid=(lo+hi)/2;if(mainCurve(mid)<P.clasp)lo=mid;else hi=mid;}return LEVER_EXTRA+lo*MAIN;})();
  // Speed of the main curve at the handoff, so the camera doesn't kink when the lid takes over.
  const handoffSpeed=(()=>{const x=(leverEnd-LEVER_EXTRA)/MAIN,e=1e-4;return (mainCurve(x+e)-mainCurve(x-e))/(2*e)/MAIN;})();
  const lerp=(a,b,t)=>a+(b-a)*t;
  const views={
   closed:{cx:631,cy:1320,sw:1000,sh:1340},
   open:{cx:640,cy:880,sw:1000,sh:1900},
   inside:{cx:631,cy:1195,sw:430,sh:430}
  };

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  // Playback cancels when less than this much of the image is on screen.
  const CANCEL_RATIO = 0.3;

  function setup(root) {
    if (root.__jarOpening) return;
    const configEl = root.querySelector('[data-jar-config]');
    const frameEl = root.querySelector('[data-jar-frame]');
    const canvas = root.querySelector('canvas');
    const link = root.matches('a[href]') ? root : root.querySelector('a[href]');
    if (!configEl || !frameEl || !canvas || !link) return;
    const cfg = JSON.parse(configEl.textContent);
    const urls = cfg.layers;
    const designMode = !!cfg.designMode || !!(window.Shopify && window.Shopify.designMode);
    const ctx = canvas.getContext('2d', { alpha: false });

    // idle -> loading -> ready -> playing -> done | cancelled (ready and cancelled can play again)
    let state = 'idle';
    let playAfterLoad = false;
    let time=0,playing=false,last=0,dirty=true,w=0,h=0,dpr=1;
    let seek = null;
    let ticking = false;
    const cleanups = [];

    function camera(u){
     const pan=sine(span(u,P.panA,P.panB)),zoom=hermite(span(u,P.zoomA,P.zoomB),0,.2);
     const fit=v=>Math.min(w/v.sw,h*.84/v.sh);
     const a=views.closed,b=views.open,c=views.inside;
     let cx=lerp(a.cx,b.cx,pan),cy=lerp(a.cy,b.cy,pan),ppu=Math.exp(lerp(Math.log(fit(a)),Math.log(fit(b)),pan));
     if(zoom>0){cx=lerp(cx,c.cx,zoom);cy=lerp(cy,c.cy,zoom);ppu=Math.exp(lerp(Math.log(ppu),Math.log(fit(c)),zoom));}
     return {cx,cy,ppu};
    }

    async function load(name){const img=new Image();await new Promise((ok,fail)=>{img.onload=ok;img.onerror=fail;img.src=urls[name];});return {img,...G.layers[name]};}

    async function init(){
     const L=Object.fromEntries(await Promise.all(Object.keys(urls).map(async k=>[k,await load(k)])));
     const off=[0,1,2].map(()=>document.createElement('canvas'));
     const offCtx=off.map(c=>c.getContext('2d'));
     let sized;
     const firstSize=new Promise(r=>sized=r);
     const observer=new ResizeObserver(()=>{const box=frameEl.getBoundingClientRect();w=box.width;h=box.height;dpr=Math.min(devicePixelRatio,2);for(const c of [canvas,...off]){c.width=Math.round(w*dpr);c.height=Math.round(h*dpr);}dirty=true;if(w&&h)sized();kick();});observer.observe(canvas.parentElement);
     cleanups.push(()=>observer.disconnect());
     let cam;
     const apply=c=>{c.setTransform(dpr*cam.ppu,0,0,dpr*cam.ppu,dpr*(w/2-cam.cx*cam.ppu),dpr*(h*.46-cam.cy*cam.ppu));c.imageSmoothingQuality='high';};
     const reset=c=>{c.setTransform(1,0,0,1,0,0);c.globalAlpha=1;c.globalCompositeOperation='source-over';c.clearRect(0,0,c.canvas.width,c.canvas.height);};
     const draw=(c,layer,alpha=1)=>{if(alpha<=0)return;c.globalAlpha=alpha;c.drawImage(layer.img,layer.x,layer.y);c.globalAlpha=1;};

     // A photograph with its clasp column re-mapped by piecewise-linear y control points.
     function warped(c,layer,src,dst){
      reset(c);apply(c);draw(c,layer);
      const k=G.clasp,x0=k.x0-k.feather,x1=k.x1+k.feather;
      const col=offCtx[2];reset(col);apply(col);
      const ys=[layer.y,...src,layer.y+layer.h],yd=[layer.y,...dst,layer.y+layer.h];
      for(let i=0;i<ys.length-1;i++){
       const sy=ys[i]-layer.y,sh=ys[i+1]-ys[i];if(sh<=0)continue;
       col.drawImage(layer.img,x0-layer.x,sy,x1-x0,sh,x0,yd[i],x1-x0,yd[i+1]-yd[i]+.5);
      }
      const g=col.createLinearGradient(x0,0,x1,0),f=k.feather/(x1-x0);
      g.addColorStop(0,'rgba(0,0,0,0)');g.addColorStop(f,'#000');g.addColorStop(1-f,'#000');g.addColorStop(1,'rgba(0,0,0,0)');
      col.globalCompositeOperation='destination-in';col.fillStyle=g;col.fillRect(x0,layer.y,x1-x0,layer.h);
      c.globalCompositeOperation='destination-out';c.fillStyle=g;c.fillRect(x0,layer.y,x1-x0,layer.h);
      // additive so L*(1-m) + W*m stays fully opaque across the feather (source-over leaves a see-through seam)
      c.globalCompositeOperation='lighter';c.setTransform(1,0,0,1,0,0);c.drawImage(col.canvas,0,0);c.globalCompositeOperation='source-over';
      return c.canvas;
     }
     function morph(a,b,p,pos){
      const k=G.clasp,mid=k[a].map((v,i)=>lerp(v,k[b][i],pos));
      const A=warped(offCtx[0],L[a],k[a],mid),B=warped(offCtx[1],L[b],k[b],mid);
      ctx.setTransform(1,0,0,1,0,0);ctx.drawImage(A,0,0);
      ctx.globalAlpha=between(p,.3,.72);ctx.drawImage(B,0,0);ctx.globalAlpha=1;
     }

     // Lid on a horizontal hinge, seen by a pinhole camera; both faces are real photographs.
     const S=G.swing,J=G.jar,R=G.lidR,d=J[1]-S.pivot,rad=Math.PI/180,thetaEnd=S.thetaEnd*rad;
     const proj=(v,th)=>{const s=S.H/(S.H-v*Math.sin(th));return {s,y:S.optic[1]+(S.pivot+v*Math.cos(th)-S.optic[1])*s};};
     const px=(X,s)=>S.optic[0]+(X-S.optic[0])*s;
     const sEndD=proj(d,thetaEnd).s;
     function lid(c,th){
      reset(c);apply(c);
      const top=proj(d+R,th).y>proj(0,th).y;
      const tex=top?L.lidTop:L.lidUnder;
      const v0=L.lidTop.y-S.pivot,v1=L.lidTop.y+L.lidTop.h-S.pivot,N=96,overlap=1.2/cam.ppu;
      const settle=ease((th/thetaEnd-.8)/.2);
      for(let i=0;i<N;i++){
       const va=lerp(v0,v1,i/N),vb=lerp(v0,v1,(i+1)/N),vm=(va+vb)/2;
       const pa=proj(va,th),pb=proj(vb,th),sm=proj(vm,th).s;
       let sy0,sy1,dx0,dw;
       if(top){
        sy0=S.pivot+va-tex.y;sy1=S.pivot+vb-tex.y;
        dx0=px(tex.x,sm);dw=tex.w*sm;
       }else{
        const ea=proj(va,thetaEnd),eb=proj(vb,thetaEnd),em=proj(vm,thetaEnd).s;
        sy0=eb.y-tex.y;sy1=ea.y-tex.y;
        // Flat lid x for this texture column, from the open photograph's measured lid width.
        const kx=S.openLidScaleX*em/sEndD,X0=J[0]+(tex.x-S.openLidCx)/kx,X1=J[0]+(tex.x+tex.w-S.openLidCx)/kx;
        dx0=lerp(px(X0,sm),tex.x,settle);dw=lerp((X1-X0)*sm,tex.w,settle);
       }
       const lo=Math.min(sy0,sy1),hi=Math.max(sy0,sy1),cs0=Math.max(0,lo),cs1=Math.min(tex.h,hi);
       if(cs1-cs0<=.01)continue;
       const ya=Math.min(pa.y,pb.y),dh=Math.max(pa.y,pb.y)-ya;
       const dy0=ya+dh*(cs0-lo)/(hi-lo),dy1=ya+dh*(cs1-lo)/(hi-lo);
       c.drawImage(tex.img,0,cs0,tex.w,cs1-cs0,dx0,dy0,dw,dy1-dy0+overlap);
      }
      // Light falls off as the lid stands up; zero at both photographed end states.
      const shade=.26*Math.pow(Math.sin(Math.PI*clamp(th/thetaEnd)),1.4);
      if(shade>0){c.globalCompositeOperation='source-atop';c.fillStyle=`rgba(40,32,24,${shade})`;c.fillRect(0,0,G.canvas[0],G.canvas[1]);c.globalCompositeOperation='source-over';}
      return c.canvas;
     }
     function swing(u){
      const th=thetaEnd*hermite(u,1.3,.3);
      apply(ctx);draw(ctx,L.base);
      draw(ctx,L.surround,1-ease(th/(40*rad)));
      // soft shadow on the jar while the lid is raised
      const lift=Math.sin(Math.PI*clamp(th/thetaEnd));
      if(lift>.01){const g=ctx.createRadialGradient(J[0],S.pivot+60,20,J[0],S.pivot+60,520);g.addColorStop(0,`rgba(60,50,40,${.16*lift})`);g.addColorStop(1,'rgba(60,50,40,0)');ctx.fillStyle=g;ctx.fillRect(J[0]-560,S.pivot-500,1120,1100);}
      const c=lid(offCtx[0],th);ctx.setTransform(1,0,0,1,0,0);ctx.drawImage(c,0,0);
     }
     function render(){
      if(!w||!h)return;
      const q=clamp(time/leverEnd);
      const u=time<leverEnd?P.clasp*hermite(q,.2,handoffSpeed*leverEnd/P.clasp):mainCurve((time-LEVER_EXTRA)/MAIN);
      cam=camera(u);
      ctx.setTransform(1,0,0,1,0,0);ctx.fillStyle=PAPER;ctx.fillRect(0,0,canvas.width,canvas.height);
      // slow start: the lever eases out to full extension, then eases back as the clasp lets go
      if(q<.62){const p=span(q,0,.62);morph('closed','lever',p,hermite(p,.5,0));}
      else if(time<leverEnd){const p=span(q,.62,1);morph('lever','released',p,hermite(p,0,1.4));}
      else swing(span(u,P.clasp,P.swing));
     }
     seek=t=>{time=Math.max(0,Math.min(duration,Number(t)||0));dirty=true;render();};
     renderFrame=render;

     await firstSize;
     // Draw every stage once so image uploads happen before playback, not during the first frames.
     for(const t of [.3,.9,1.2,1.6,2.1,2.7,0])seek(t);
    }

    let renderFrame = null;

    // Frame loop. Runs only while playing or when a redraw is pending (resize), not continuously.
    function tick(now){
     if(playing){
      time+=(now-last)/1000;
      if(time>=duration){time=duration;playing=false;dirty=true;last=now;if(renderFrame)renderFrame();dirty=false;ticking=false;finished();return;}
      dirty=true;
     }
     last=now;
     if(dirty&&renderFrame){renderFrame();dirty=false;}
     if(playing)requestAnimationFrame(tick);else ticking=false;
    }
    function kick(){if(ticking)return;ticking=true;last=performance.now();requestAnimationFrame(tick);}

    const setState=v=>{state=v;root.setAttribute('data-jar-state',v);};
    function play(){
     if(!seek)return;
     seek(0);
     root.classList.remove('is-loading');root.classList.add('is-animating');
     setState('playing');playing=true;last=performance.now();
     kick();
    }
    // Stop playback and hold the current frame. The next click starts over.
    function cancel(){
     if(state!=='playing')return;
     playing=false;setState('cancelled');
    }
    // Back to the closed jar, as on first load.
    function resetClosed(){
     playing=false;playAfterLoad=false;
     if(seek){seek(0);setState('ready');}
     root.classList.remove('is-animating','is-loading');
    }
    const go=()=>{window.location.assign(link.href);};
    // Only reached after a natural, uninterrupted playthrough.
    function finished(){
     setState('done');
     root.dispatchEvent(new CustomEvent('jaranimationend',{bubbles:true}));
     if(designMode||!cfg.redirect)return;
     go();
    }

    async function prepare(){
     if(state!=='idle')return;
     setState('loading');
     try{await init();}
     catch(e){console.error('[jar-opening] The photographs could not load.',e);setState('error');root.classList.remove('is-loading');if(playAfterLoad)window.location.assign(link.href);return;}
     setState('ready');
     root.classList.add('is-ready');
     if(playAfterLoad){playAfterLoad=false;play();}
    }

    // Fetch the layers on the first sign of intent, so a click usually finds them ready.
    const intent=()=>{prepare();};
    root.addEventListener('pointerenter',intent,{once:true});
    root.addEventListener('touchstart',intent,{once:true,passive:true});
    root.addEventListener('focusin',intent,{once:true});

    link.addEventListener('click',e=>{
     // New tab, new window, download: leave the link alone.
     if(e.defaultPrevented||e.button!==0||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey)return;
     // Reduced motion, or the photographs failed: go straight to the page.
     if(reduced.matches||state==='error')return;
     e.preventDefault();
     // A second click while it plays (or once it has finished) goes straight to the page.
     if(state==='playing'||state==='done'){if(designMode)return;go();return;}
     if(state==='idle'||state==='loading'){playAfterLoad=true;root.classList.add('is-loading');prepare();return;}
     play();
    });

    if('IntersectionObserver' in window){
     const vis=new IntersectionObserver(entries=>{
      for(const e of entries)if(state==='playing'&&e.intersectionRatio<CANCEL_RATIO)cancel();
      for(const e of entries)if(playAfterLoad&&e.intersectionRatio<CANCEL_RATIO){playAfterLoad=false;root.classList.remove('is-loading');}
     },{threshold:[0,.1,.2,CANCEL_RATIO,.5,1]});
     vis.observe(frameEl);
     cleanups.push(()=>vis.disconnect());
    }

    const onVisibility=()=>{if(document.hidden){cancel();playAfterLoad=false;}};
    document.addEventListener('visibilitychange',onVisibility);
    // Coming back with Back: show the closed jar again, ready for another click.
    const onPageShow=e=>{if(e.persisted)resetClosed();};
    window.addEventListener('pageshow',onPageShow);
    const onReduced=()=>{if(reduced.matches&&state==='playing')resetClosed();};
    reduced.addEventListener('change',onReduced);
    cleanups.push(()=>{document.removeEventListener('visibilitychange',onVisibility);window.removeEventListener('pageshow',onPageShow);reduced.removeEventListener('change',onReduced);playing=false;});

    root.__jarOpening={destroy:()=>cleanups.forEach(f=>f())};
  }

  const scan = (scope) => (scope || document).querySelectorAll('[data-jar-opening]').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-jar-opening]').forEach((el) => el.__jarOpening && el.__jarOpening.destroy && el.__jarOpening.destroy());
  });
})();
